<script lang="ts">
  import { currentPath } from './lib/router.svelte';
  import Landing from './routes/Landing.svelte';
  import Audit from './routes/Audit.svelte';
  import Register from './routes/Register.svelte';
  import AdminLogin from './routes/AdminLogin.svelte';
  import ForgotPassword from './routes/ForgotPassword.svelte';
  import ResetPassword from './routes/ResetPassword.svelte';
  import InviteClaim from './routes/InviteClaim.svelte';
  import AdminConversations from './routes/AdminConversations.svelte';
  import AdminAIInsights from './routes/AdminAIInsights.svelte';
  import AdminContent from './routes/AdminContent.svelte';
  import AdminCommunityTimeline from './routes/AdminCommunityTimeline.svelte';
  import AdminMembers from './routes/AdminMembers.svelte';
  import AdminMember from './routes/AdminMember.svelte';
  import AdminInsights from './routes/AdminInsights.svelte';
  import AdminDiscovery from './routes/AdminDiscovery.svelte';
  import AdminWeeklyDigest from './routes/AdminWeeklyDigest.svelte';
  import AdminInsightsRetention from './routes/AdminInsightsRetention.svelte';
  import AdminInsightsContributors from './routes/AdminInsightsContributors.svelte';
  import AdminPreferences from './routes/AdminPreferences.svelte';
  import AdminAffiliate from './routes/AdminAffiliate.svelte';
  import AdminAffiliates from './routes/AdminAffiliates.svelte';
  import AdminModeration from './routes/AdminModeration.svelte';
  import AdminPipelines from './routes/AdminPipelines.svelte';
  import AdminAutomations from './routes/AdminAutomations.svelte';
  import WebhookDocs from './routes/WebhookDocs.svelte';
  import AdminExpired from './routes/AdminExpired.svelte';
  import AdminDmInboxes from './routes/AdminDmInboxes.svelte';
  import AdminInvites from './routes/AdminInvites.svelte';
  import AdminAccounts from './routes/AdminAccounts.svelte';
  import AdminAuditLeads from './routes/AdminAuditLeads.svelte';
  import AdminHealth from './routes/AdminHealth.svelte';
  import { navigate } from './lib/router.svelte';
  import { getToken, DISCOVERY_PATH } from './lib/admin';

  const path = $derived(currentPath());

  // Session gate. Every /admin route except the login page needs a token; without
  // one we must go straight to login rather than mount the shell. Re-reads on every
  // path change (login/logout both navigate), so it flips correctly after sign-in.
  const needsAuth = $derived(path.startsWith('/admin') && path !== '/admin/login');
  const hasToken = $derived.by(() => {
    void path;
    return !!getToken();
  });

  // Correct the address bar + surface the "please sign in" notice. The render gate
  // below already prevents any admin page (and its menu) from mounting token-less —
  // this just makes the URL honest. Without it, a page's own init() would call
  // navigate() mid-mount, which races the route swap and can leave the shell up with
  // an infinite spinner (loading starts true and that path returns before clearing).
  $effect(() => {
    if (needsAuth && !hasToken) navigate('/admin/login');
  });

  // The old /admin/community posts reader was retired in favour of the timeline,
  // which supports richer filtering and already parses the legacy ?label=/?date=/
  // ?post= deep-links. Redirect the bare route (bookmarks, stray links) there,
  // carrying the query string so a deep link still lands pre-filtered.
  $effect(() => {
    if (path === '/admin/community') navigate('/admin/community/timeline' + window.location.search);
  });

  // The standalone Reply Queue page was retired — the queue + sent history now live
  // under the "Outbox Queue" tab on Posts & Comments. Redirect old links/bookmarks
  // straight to that tab.
  $effect(() => {
    if (path.startsWith('/admin/moderation/queue')) navigate('/admin/moderation?tab=outbox');
  });
  // Signup's old wizard and card-gated trial page. Signup carries on in Discovery now, so
  // a bookmark or a stale link to either lands there — `?add` included, which is how the
  // sidebar asked to claim another community.
  $effect(() => {
    if (path === '/admin/onboarding' || path === '/admin/trial') {
      navigate(DISCOVERY_PATH + window.location.search);
    }
  });
  // `Questions & gaps` went with the clustering pipeline. A bookmark to it should open
  // what replaced it, not a blank page.
  $effect(() => {
    if (path.startsWith('/admin/insights/questions')) navigate('/admin/insights/digest');
  });
</script>

{#if path === '/register'}
  <Register />
{:else if path === '/invite'}
  <!-- The magic link. PUBLIC and above the session gate: the token in the URL is the
       credential, and the whole point is that the visitor has no session yet. -->
  <InviteClaim />
{:else if path === '/admin/login'}
  <AdminLogin />
{:else if path === '/forgot-password'}
  <!-- PUBLIC: no session — request a reset-link email. -->
  <ForgotPassword />
{:else if path === '/webhooks'}
  <!-- PUBLIC: the webhook contract. The reader is usually not our customer at all — it is
       the developer their customer handed the integration to, and putting a field table
       behind a login helps nobody. -->
  <WebhookDocs />
{:else if path === '/audit'}
  <!-- PUBLIC: the keyword audit lead magnet. No session, no token, and deliberately
       above the auth gate — the visitor is a stranger and that is the entire point. -->
  <Audit />
{:else if path === '/reset-password'}
  <!-- PUBLIC: the token in the URL is the credential; sets a new password and signs in. -->
  <ResetPassword />
{:else if needsAuth && !hasToken}
  <!-- No session → render login immediately; never mount the admin shell/menu
       token-less (that path stranded new users on a spinner behind the menu). -->
  <AdminLogin />
{:else if path.startsWith('/admin/conversations')}
  <AdminConversations />
{:else if path.startsWith('/admin/content')}
  <AdminContent />
{:else if path.startsWith('/admin/preferences/affiliates')}
  <!-- Registered BEFORE /admin/preferences so this more-specific path wins; the
       sidebar's prefsActive startsWith check still lights Preferences for it. -->
  <AdminAffiliate />
{:else if path.startsWith('/admin/preferences')}
  <AdminPreferences />
{:else if path.startsWith('/admin/pipelines')}
  <AdminPipelines />
{:else if path.startsWith('/admin/automations')}
  <AdminAutomations />
{:else if path.startsWith('/admin/moderation/queue')}
  <!-- Retired route — the $effect above redirects to /admin/moderation. -->
{:else if path.startsWith('/admin/moderation')}
  <AdminModeration />
{:else if path.startsWith('/admin/community/members/')}
  <AdminMember />
{:else if path.startsWith('/admin/community/members')}
  <AdminMembers />
{:else if path.startsWith('/admin/community/timeline')}
  <AdminCommunityTimeline />
{:else if path.startsWith('/admin/community')}
  <!-- Retired route — the $effect above redirects to /admin/community/timeline. -->
{:else if path.startsWith('/admin/insights/discovery')}
  <AdminDiscovery />
{:else if path.startsWith('/admin/insights/digest')}
  <AdminWeeklyDigest />
{:else if path.startsWith('/admin/insights/questions')}
  <!-- Retired with the clustering pipeline — the $effect above redirects to the digest. -->
{:else if path.startsWith('/admin/insights/engagement')}
  <AdminInsightsRetention />
{:else if path.startsWith('/admin/insights/contributors')}
  <AdminInsightsContributors />
{:else if path.startsWith('/admin/insights/ai')}
  <AdminAIInsights />
{:else if path.startsWith('/admin/overview')}
  <AdminInsights />
{:else if path.startsWith('/admin/insights')}
  <AdminInsights />
{:else if path.startsWith('/admin/expired')}
  <AdminExpired />
{:else if path.startsWith('/admin/health')}
  <AdminHealth />
{:else if path.startsWith('/admin/invites')}
  <AdminInvites />
{:else if path.startsWith('/admin/dm-inboxes')}
  <!-- Superadmin DM mirror explorer. Before the /admin catch-all, like its siblings. -->
  <AdminDmInboxes />
{:else if path.startsWith('/admin/audit-leads')}
  <!-- Superadmin: the public audit's lead log. Before the /admin catch-all, like its
       siblings. -->
  <AdminAuditLeads />
{:else if path.startsWith('/admin/accounts')}
  <!-- Superadmin accounts console. Must sit before the /admin catch-all below. -->
  <AdminAccounts />
{:else if path.startsWith('/admin/affiliates')}
  <!-- Superadmin roster. Must sit before the /admin catch-all below. -->
  <AdminAffiliates />
{:else if path.startsWith('/admin')}
  <!-- Default admin landing is the Overview (top-level home of /admin). -->
  <AdminInsights />
{:else}
  <Landing />
{/if}
